'use client'

import {
  Animation,
  Button,
  Card,
  Cell,
  Divider,
  EmptyState,
  Pagination,
  Text,
} from '@vinted/web-ui'

import { reloadPage, scrollToTop, useLocation } from '@marketplace-web/shared/browser'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { ContentLoader } from '@marketplace-web/shared/ui-helpers'
import { useAsset } from '@marketplace-web/domain/assets'
import InfoBanner from 'components/InfoBanner'
import UserGrid from 'components/UserGrid'

import { MEMBER_FOLLOWING_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import useFetchUserById from 'hooks/useFetchUserById'

import useFetchUsers from './hooks/useFetchUsers'
import ItemUserInfo from './ItemUserInfo'

const DEFAULT_PAGE = 1

type Props = {
  id: string
}

const FollowedUsers = ({ id }: Props) => {
  const userId = Number(id)
  const translate = useTranslate('user.followed_users')
  const { data: userData } = useFetchUserById({ id: userId, localize: true })
  const currentUserId = useSession()?.user?.id
  const location = useLocation()
  const asset = useAsset('/assets/animations')

  const viewingYourFollowers = userId === currentUserId

  const currentPage = Number(location.urlParams.page) || DEFAULT_PAGE
  const { data, error, isLoading, fetchUsers } = useFetchUsers(userId, currentPage)

  const setNewPage = (newPage: number) => {
    fetchUsers({ userId, page: newPage })
    location.pushHistoryState(MEMBER_FOLLOWING_URL(userId, newPage))
    scrollToTop()
  }

  const renderPagination = () => {
    if (!data || data.pagination.totalPages <= 1) return null

    return (
      <Card>
        <Pagination
          currentPage={currentPage}
          pageCount={data.pagination.totalPages}
          onNextClick={setNewPage}
          onPrevClick={setNewPage}
          onPageClick={setNewPage}
          testId="followed-users-pagination"
        />
      </Card>
    )
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault()

      return
    }

    reloadPage()
  }

  const renderBodyContent = () => {
    if (isLoading) {
      return (
        <div className="followed-users__body-state--center">
          <ContentLoader size={ContentLoader.Size.X2Large} testId="followed-users-loader" />
        </div>
      )
    }

    if (error) {
      return (
        <div className="followed-users__body-state--center">
          <EmptyState
            title={translate('error.title')}
            body={translate('error.body')}
            action={
              <Button
                isLoading={isLoading}
                text={translate('error.cta')}
                styling={Button.Styling.Filled}
                onClick={handleClick}
              />
            }
          />
        </div>
      )
    }

    if (!data?.users.length) {
      return (
        <div className="followed-users__body-state--center">
          <EmptyState
            animation={<Animation animationUrl={asset('search-empty-state.json')} />}
            title={translate('empty_state.title', { username: userData?.user.login })}
          />
        </div>
      )
    }

    return (
      <UserGrid
        users={data.users}
        showFollowButtons={viewingYourFollowers}
        testId="followed-users-grid"
      />
    )
  }

  return (
    <>
      {viewingYourFollowers && (
        <InfoBanner
          screen={Screen.Following}
          linkTracking={{ target: ClickableElement.MergeFollowingBanner }}
          theme="inverseExperimental"
        />
      )}
      <aside className="body-content__sidebar">
        {userData?.user && (
          <ItemUserInfo
            user={userData.user}
            showActions={!viewingYourFollowers}
            testId="followed-users-user"
          />
        )}
      </aside>
      <div className="body-content__content">
        <div className="followed-users__content">
          {userData?.user && (
            <>
              <Cell
                title={
                  <Text
                    as="h2"
                    text={translate('title', { username: userData?.user.login })}
                    type={Text.Type.Title}
                  />
                }
                styling={Cell.Styling.Narrow}
              />
              <Divider />
            </>
          )}
          <div className="followed-users__body">{renderBodyContent()}</div>
          {renderPagination()}
        </div>
      </div>
    </>
  )
}

export default FollowedUsers
